import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { affiliateAPI } from '@/lib/api';
import { CREDIT_PLANS, AFFILIATE_COMMISSION_RATE } from '@/lib/constants';
import { 
  Users, DollarSign, Copy, Check, TrendingUp, Clock, 
  Wallet, ArrowRight, Gift, Share2, ExternalLink
} from 'lucide-react';

// Commission earnings per plan (30%)
const COMMISSION_TABLE = CREDIT_PLANS.map(plan => ({
  plan: plan.name,
  price: plan.price,
  commission: Math.round(plan.price * (AFFILIATE_COMMISSION_RATE / 100))
}));

const AffiliatePanel = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await affiliateAPI.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load affiliate dashboard:', error);
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    const link = `${window.location.origin}${dashboardData?.referral_link}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handlePayoutRequest = async () => {
    if (!paymentDetails.trim()) {
      toast.error('Please enter payment details');
      return;
    }

    setIsRequesting(true);
    try {
      const response = await affiliateAPI.requestPayout(paymentMethod, paymentDetails);
      toast.success(response.data.message);
      setShowPayoutModal(false);
      setPaymentDetails('');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setIsRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const referralLink = `${window.location.origin}${dashboardData?.referral_link || ''}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift className="w-6 h-6 text-green-400" />
            Affiliate Program
          </h2>
          <p className="text-gray-400 mt-1">
            Earn <span className="text-green-400 font-bold">{AFFILIATE_COMMISSION_RATE}% commission</span> on every referral purchase
          </p>
        </div>
        <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-lg px-3 py-1">
          🔥 {AFFILIATE_COMMISSION_RATE}%
        </Badge>
      </div>

      {/* Commission Table */}
      <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg">💵 Your Earnings Per Sale</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            {COMMISSION_TABLE.map((item, i) => (
              <div key={i} className="p-3 bg-white/5 rounded-lg">
                <p className="text-gray-400 text-sm">{item.plan}</p>
                <p className="text-gray-500 text-xs">₹{item.price} sold</p>
                <p className="text-2xl font-bold text-green-400">₹{item.commission}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 text-sm mt-3">
            💡 Most affiliates promote the Creator plan (₹599) for fastest sales
          </p>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Referrals</p>
                <p className="text-2xl font-bold text-white">{stats.total_referrals || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Earnings</p>
                <p className="text-2xl font-bold text-white">₹{(stats.total_earnings || 0).toFixed(2)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pending Earnings</p>
                <p className="text-2xl font-bold text-white">₹{(stats.pending_earnings || 0).toFixed(2)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Paid Out</p>
                <p className="text-2xl font-bold text-white">₹{(stats.paid_earnings || 0).toFixed(2)}</p>
              </div>
              <Wallet className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link */}
      <Card className="bg-white/5 border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Your Referral Link
          </CardTitle>
          <CardDescription>Share this link and earn {AFFILIATE_COMMISSION_RATE}% on every purchase</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={referralLink}
              readOnly
              className="bg-white/10 border-white/20 text-white font-mono text-sm"
            />
            <Button
              onClick={copyReferralLink}
              className="bg-green-600 hover:bg-green-700 min-w-[100px]"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">
              Code: {dashboardData?.referral_code}
            </Badge>
            <span>•</span>
            <span className="text-green-400">{AFFILIATE_COMMISSION_RATE}% commission per sale</span>
          </div>

          <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
            <p className="text-purple-300 text-sm">
              📣 Share on WhatsApp, Instagram, YouTube, blogs - anywhere your audience is!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payout Section */}
      {stats.pending_earnings >= 1000 && (
        <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-full">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">You have ₹{stats.pending_earnings.toFixed(2)} ready for payout!</p>
                <p className="text-gray-400 text-sm">Minimum payout: ₹1,000</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowPayoutModal(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              Request Payout
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs for History */}
      <Tabs defaultValue="commissions" className="space-y-4">
        <TabsList className="bg-white/10">
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
        </TabsList>

        <TabsContent value="commissions">
          <Card className="bg-white/5 border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Commission History</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.commissions?.length > 0 ? (
                <div className="space-y-2">
                  {dashboardData.commissions.map((commission, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium">₹{commission.amount.toFixed(2)}</p>
                        <p className="text-gray-400 text-sm">
                          {new Date(commission.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge 
                        className={
                          commission.status === 'paid' 
                            ? 'bg-green-500/20 text-green-300' 
                            : commission.status === 'processing'
                            ? 'bg-blue-500/20 text-blue-300'
                            : 'bg-amber-500/20 text-amber-300'
                        }
                      >
                        {commission.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">No commissions yet. Share your link to start earning!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card className="bg-white/5 border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Your Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.referred_users?.length > 0 ? (
                <div className="space-y-2">
                  {dashboardData.referred_users.map((referral, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Users className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{referral.name}</p>
                          <p className="text-gray-400 text-sm">{referral.email}</p>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">No referrals yet. Share your link to invite users!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="bg-gray-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
            <DialogDescription>
              Enter your payment details to receive ₹{stats.pending_earnings?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-white/10 border-white/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>
                {paymentMethod === 'upi' ? 'UPI ID' : 'Bank Account Details'}
              </Label>
              <Input
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                placeholder={paymentMethod === 'upi' ? 'yourname@upi' : 'Account Number, IFSC Code'}
                className="bg-white/10 border-white/20"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePayoutRequest}
              disabled={isRequesting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isRequesting ? 'Processing...' : 'Request Payout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliatePanel;
